import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Checkbox,
  Button,
  Space,
  Upload,
  Image,
  Divider,
  Typography,
  message,
  Select,
  Tag,
  Alert,
  Row,
  Col,
  Card,
} from 'antd';
import {
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import authApi from '../../services/authApi';
import { EstimateLineItems } from '../Estimates';
import { EstimateLineItem } from '../../types';
import { useIsMobile } from '../../hooks/useIsMobile';
import ImageCropModal from './ImageCropModal';
import VideoPreviewModal from './VideoPreviewModal';
import { getErrorMessage } from '../../utils/apiError';
import { reportSaveError } from '../../utils/saveErrorNotice';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface EstimateTemplate {
  id?: number;
  template_name: string;
  category: string;
  description?: string;
  is_public: boolean;
  is_active: boolean;
  estimated_duration_hours: number;
  image_url?: string;
  video_url?: string;
  tags?: string[];
  line_items?: EstimateLineItem[];
  vehicle_year?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  is_general?: boolean;
}

interface EstimateTemplateFormProps {
  visible: boolean;
  template?: EstimateTemplate | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EstimateTemplateForm: React.FC<EstimateTemplateFormProps> = ({
  visible,
  template,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [lineItems, setLineItems] = useState<EstimateLineItem[]>([]);
  const [taxRate, setTaxRate] = useState(7);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [videoUrl, setVideoUrl] = useState<string | undefined>(undefined);
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string>('');
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [tempVideoFile, setTempVideoFile] = useState<File | null>(null);
  const [tempVideoUrl, setTempVideoUrl] = useState<string>('');

  const isEditing = Boolean(template?.id);
  const isMobile = useIsMobile(768);

  useEffect(() => {
    if (visible) {
      fetchCategories();
      if (template) {
        // Populate form for editing
        form.setFieldsValue({
          template_name: template.template_name,
          category: template.category,
          description: template.description || '',
          estimated_duration_hours: template.estimated_duration_hours,
          is_public: template.is_public,
          is_active: template.is_active,
          image_url: template.image_url || '',
          tags: template.tags || [],
          vehicle_year: template.vehicle_year || '',
          vehicle_make: template.vehicle_make || '',
          vehicle_model: template.vehicle_model || '',
          is_general: template.is_general || false,
        });
        setLineItems(template.line_items || []);
        setImageUrl(template.image_url);
        setVideoUrl(template.video_url);
      } else {
        // Reset for new template
        form.resetFields();
        setLineItems([]);
        setImageUrl(undefined);
        setVideoUrl(undefined);
        form.setFieldsValue({
          is_public: true,
          is_active: true,
          estimated_duration_hours: 1,
          is_general: false,
        });
      }
    }
  }, [visible, template, form]);

  const fetchCategories = async () => {
    try {
      const response = await authApi.get('/admin/template-categories');
      // Extract category names from the response
      const categoryNames = response.data.map((cat: any) => cat.name);
      setCategories(categoryNames);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Set default categories if fetch fails
      setCategories(['General', 'Maintenance', 'Repair', 'Diagnostic']);
    }
  };

  const handleLineItemsChange = (items: EstimateLineItem[]) => {
    setLineItems(items);
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.total_price, 0);
    const taxAmount = (subtotal * taxRate) / 100;
    const total = subtotal + taxAmount;
    return { subtotal, taxRate, taxAmount, total };
  };

  const handleImageSelect = (file: File) => {
    // Create a temporary URL for the crop modal
    const tempUrl = URL.createObjectURL(file);
    setTempImageUrl(tempUrl);
    setShowCropModal(true);
    return false; // Prevent default upload behavior
  };

  const handleCropSave = async (croppedBlob: Blob, croppedUrl: string) => {
    setUploadingImage(true);
    setShowCropModal(false);

    try {
      // Convert blob to file
      const file = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('file', file);

      const response = await authApi.post('/admin/upload/template-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Construct the full URL for the uploaded file
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const baseUrl = apiUrl.replace(/\/api$/, ''); // Remove /api from the end
      const url = `${baseUrl}${response.data.url}`;

      console.log('Image uploaded:', { baseUrl, responseUrl: response.data.url, finalUrl: url });
      setImageUrl(url);

      // Clean up temp URL
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl('');

      message.success('Image uploaded successfully');
    } catch (error: any) {
      console.error('Image upload error:', error);
      message.error(error?.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    // Clean up temp URL
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl('');
    }
  };

  const handleVideoSelect = (file: File) => {
    // Create a temporary URL for the preview modal
    const tempUrl = URL.createObjectURL(file);
    setTempVideoFile(file);
    setTempVideoUrl(tempUrl);
    setShowVideoPreview(true);
    return false; // Prevent default upload behavior
  };

  const handleVideoConfirm = async (file: File) => {
    setUploadingVideo(true);
    setShowVideoPreview(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authApi.post('/admin/upload/template-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Construct the full URL for the uploaded file
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const baseUrl = apiUrl.replace(/\/api$/, ''); // Remove /api from the end
      const url = `${baseUrl}${response.data.url}`;

      console.log('Video uploaded:', { baseUrl, responseUrl: response.data.url, finalUrl: url });
      setVideoUrl(url);

      // Clean up temp URL
      URL.revokeObjectURL(tempVideoUrl);
      setTempVideoUrl('');
      setTempVideoFile(null);

      message.success('Video uploaded successfully');
    } catch (error: any) {
      console.error('Video upload error:', error);
      message.error(error?.response?.data?.error || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleVideoPreviewCancel = () => {
    setShowVideoPreview(false);
    // Clean up temp URL
    if (tempVideoUrl) {
      URL.revokeObjectURL(tempVideoUrl);
      setTempVideoUrl('');
      setTempVideoFile(null);
    }
  };

  const handleDeleteImage = () => {
    setImageUrl(undefined);
    message.success('Image removed');
  };

  const handleDeleteVideo = () => {
    setVideoUrl(undefined);
    message.success('Video removed');
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      if (lineItems.length === 0) {
        message.error('Please add at least one line item to the template');
        return;
      }

      setLoading(true);

      // Extract category (it's an array due to tags mode with maxCount=1)
      const categoryValue = Array.isArray(values.category) ? values.category[0] : values.category;

      // If category doesn't exist in our list, create it
      if (categoryValue && !categories.includes(categoryValue)) {
        try {
          await authApi.post('/admin/template-categories', {
            name: categoryValue,
            description: `Auto-created category for ${categoryValue}`,
            display_order: categories.length,
            is_active: true
          });
          message.success(`Category "${categoryValue}" created`);
          // Refresh categories list
          await fetchCategories();
        } catch (error: any) {
          // If category already exists, that's fine, continue.
          //
          // WP-03: this was `data?.error?.includes('already exists')`. On any
          // uncaught 500 the central handler put an OBJECT in `data.error`,
          // `.includes` was `undefined`, and this line threw a TypeError
          // INSIDE a catch block — turning a recoverable "category exists"
          // into an unhandled rejection that killed the whole template save.
          // The backend now guarantees `error` is a string; `getErrorMessage`
          // guarantees it here regardless of what the server sends.
          if (!getErrorMessage(error, '').includes('already exists')) {
            console.error('Error creating category:', error);
          }
        }
      }

      const { subtotal, taxAmount, total } = calculateTotals();

      const templateData = {
        template_name: values.template_name,
        category: categoryValue,
        description: values.description || null,
        is_public: values.is_public,
        is_active: values.is_active,
        estimated_duration_hours: values.estimated_duration_hours,
        image_url: imageUrl || null,
        video_url: videoUrl || null,
        tags: values.tags || [],
        vehicle_year: values.vehicle_year || null,
        vehicle_make: values.vehicle_make || null,
        vehicle_model: values.vehicle_model || null,
        is_general: values.is_general || false,
        subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total_amount: total,
        line_items: lineItems,
      };

      if (isEditing) {
        await authApi.put(`/admin/estimate-templates/${template!.id}`, templateData);
        message.success('Template updated successfully');
      } else {
        await authApi.post('/admin/estimate-templates', templateData);
        message.success('Template created successfully');
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error saving template:', error);
      reportSaveError(message, error, { fallback: 'Failed to save template' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setLineItems([]);
    setImageUrl(undefined);
    setVideoUrl(undefined);
    onClose();
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {isEditing ? 'Edit Estimate Template' : 'Create Estimate Template'}
        </Title>
      }
      open={visible}
      onCancel={handleClose}
      width={1400}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          <CloseOutlined /> Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          icon={<SaveOutlined />}
        >
          {isEditing ? 'Update Template' : 'Create Template'}
        </Button>,
      ]}
      style={{ top: 20 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          is_public: true,
          is_active: true,
          estimated_duration_hours: 1,
        }}
      >
        <Alert
          message="Template Information"
          description="Create reusable service templates that customers can select from your public portal. Templates will be automatically calculated with tax and totals."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Template Name"
              name="template_name"
              rules={[{ required: true, message: 'Please enter a template name' }]}
            >
              <Input placeholder="e.g., Oil Change Package, Brake Service, etc." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: 'Please select or enter a category' }]}
            >
              <Select
                placeholder="Select or type a category"
                showSearch
                allowClear
                mode="tags"
                maxCount={1}
                options={categories.map(cat => ({ label: cat, value: cat }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Description"
          name="description"
        >
          <TextArea
            rows={3}
            placeholder="Describe what this template includes..."
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Estimated Duration (hours)"
              name="estimated_duration_hours"
              rules={[{ required: true, message: 'Please enter estimated duration' }]}
            >
              <InputNumber
                min={0.5}
                max={100}
                step={0.5}
                style={{ width: '100%' }}
                placeholder="e.g., 2.5"
              />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item
              label="Tags (optional)"
              name="tags"
            >
              <Select
                mode="tags"
                placeholder="Add tags (press Enter)"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Media (Images & Videos)</Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Service Image">
              <Space direction="vertical" style={{ width: '100%' }}>
                {imageUrl ? (
                  <Card
                    size="small"
                    cover={<Image src={imageUrl} alt="Template preview" style={{ maxHeight: 200, objectFit: 'cover' }} />}
                    actions={[
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleDeleteImage}
                      >
                        Remove Image
                      </Button>
                    ]}
                  />
                ) : (
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleImageSelect}
                    disabled={uploadingImage}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={uploadingImage}
                      block
                      size="large"
                    >
                      {uploadingImage ? 'Uploading...' : 'Upload & Edit Image'}
                    </Button>
                  </Upload>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <PictureOutlined /> Recommended: 800x600px or larger (JPEG, PNG, GIF, WebP)
                </Text>
              </Space>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Service Video">
              <Space direction="vertical" style={{ width: '100%' }}>
                {videoUrl ? (
                  <Card
                    size="small"
                    cover={
                      <video
                        src={videoUrl}
                        controls
                        style={{ width: '100%', maxHeight: 200, objectFit: 'cover', backgroundColor: '#000' }}
                      />
                    }
                    actions={[
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleDeleteVideo}
                      >
                        Remove Video
                      </Button>
                    ]}
                  />
                ) : (
                  <Upload
                    accept="video/*"
                    showUploadList={false}
                    beforeUpload={handleVideoSelect}
                    disabled={uploadingVideo}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={uploadingVideo}
                      block
                      size="large"
                    >
                      {uploadingVideo ? 'Uploading...' : 'Upload & Preview Video'}
                    </Button>
                  </Upload>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <PlayCircleOutlined /> Max 50MB (MP4, WebM, OGG, MOV)
                </Text>
              </Space>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Vehicle Compatibility</Divider>

        <Form.Item
          name="is_general"
          valuePropName="checked"
        >
          <Checkbox>
            <Space direction="vertical" size={0}>
              <Text strong>General Template (Applies to All Vehicles)</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Check this if the template is not vehicle-specific
              </Text>
            </Space>
          </Checkbox>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.is_general !== currentValues.is_general}>
          {({ getFieldValue }) =>
            !getFieldValue('is_general') && (
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Vehicle Year"
                    name="vehicle_year"
                  >
                    <Input placeholder="e.g., 2020" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Vehicle Make"
                    name="vehicle_make"
                  >
                    <Input placeholder="e.g., Toyota" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Vehicle Model"
                    name="vehicle_model"
                  >
                    <Input placeholder="e.g., Camry" />
                  </Form.Item>
                </Col>
              </Row>
            )
          }
        </Form.Item>

        <Divider />

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="is_public"
              valuePropName="checked"
            >
              <Checkbox>
                <Space direction="vertical" size={0}>
                  <Text strong>Public Template</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Visible to customers in the public portal
                  </Text>
                </Space>
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="is_active"
              valuePropName="checked"
            >
              <Checkbox>
                <Space direction="vertical" size={0}>
                  <Text strong>Active</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Template is active and can be used
                  </Text>
                </Space>
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Service Line Items</Title>
          <Text type="secondary">
            Add the services and parts included in this template
          </Text>
        </div>

        <EstimateLineItems
          estimateId={template?.id || 0}
          items={lineItems}
          isMobile={isMobile}
          isLocked={false}
          onItemsChange={handleLineItemsChange}
        />

        <Divider />

        <Card size="small" style={{ backgroundColor: '#f9fafb' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Subtotal</Text>
                <Text strong style={{ fontSize: 16 }}>${subtotal.toFixed(2)}</Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Tax ({taxRate}%)</Text>
                <Text strong style={{ fontSize: 16 }}>${taxAmount.toFixed(2)}</Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Total</Text>
                <Text strong style={{ fontSize: 20, color: '#52c41a' }}>${total.toFixed(2)}</Text>
              </Space>
            </Col>
          </Row>
        </Card>
      </Form>

      {/* Image Crop Modal */}
      <ImageCropModal
        visible={showCropModal}
        imageUrl={tempImageUrl}
        onSave={handleCropSave}
        onCancel={handleCropCancel}
        aspectRatio={4 / 3} // 4:3 aspect ratio for service images
      />

      {/* Video Preview Modal */}
      {tempVideoFile && (
        <VideoPreviewModal
          visible={showVideoPreview}
          videoFile={tempVideoFile}
          videoUrl={tempVideoUrl}
          onConfirm={handleVideoConfirm}
          onCancel={handleVideoPreviewCancel}
        />
      )}
    </Modal>
  );
};

export default EstimateTemplateForm;
